/**
 * Parse inline product-reference tokens emitted by the bot LLM.
 *
 * Token format: [[ref:CART_ITEM_ID|Display Name]]
 *
 * Example input:
 *   "Added [[ref:8f3a-4c11-9999|Hilton Rome]] to your cart for €820."
 *
 * Output is an ordered array of segments suitable for rendering:
 *   [
 *     { type: 'text', value: 'Added ' },
 *     { type: 'ref',  cartItemId: '8f3a-4c11-9999', displayName: 'Hilton Rome' },
 *     { type: 'text', value: ' to your cart for €820.' },
 *   ]
 *
 * Malformed tokens (missing pipe, empty id, empty name) fall through to the
 * default text segment so we never crash on weird LLM output.
 */
export type InlineRefSegment =
  | { type: 'text'; value: string }
  | { type: 'ref'; cartItemId: string; displayName: string };

const TOKEN_RE = /\[\[ref:([^|\]]+)\|([^\]]+)\]\]/g;

export function parseInlineRefs(text: string): InlineRefSegment[] {
  if (!text) return [{ type: 'text', value: '' }];

  const out: InlineRefSegment[] = [];
  let cursor = 0;

  // Array.from() avoids needing the --downlevelIteration TS flag for
  // for...of over an iterator (matchAll returns an iterator).
  const matches = Array.from(text.matchAll(TOKEN_RE));

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const [full, cartItemId, displayName] = match;
    const idx = match.index ?? 0;
    const id = (cartItemId || '').trim();
    const name = (displayName || '').trim();

    if (!id || !name) continue;

    if (idx > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, idx) });
    }
    out.push({ type: 'ref', cartItemId: id, displayName: name });
    cursor = idx + full.length;
  }

  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) });
  }
  return out.length ? out : [{ type: 'text', value: text }];
}
